import {FC, Fragment} from 'react'
import clsx from 'clsx'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {useBranding} from '@context/Branding'
import {useLayout} from '../../../core'
import {usePageData} from '@context/PageData'
import {useFavorites} from '@context/Favorites'
import Icon from '@components/Icon'
import IconButton from '@components/IconButton'

const DefaultTitle: FC = () => {
  const {pageTitle, pageDescription, pageBreadcrumbs, hasBack, backLink} = usePageData()
  const {config, classes} = useLayout()
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const {isFavorite, addFavorite, removeByPath} = useFavorites()
  const {companyName} = useBranding()
  const isFav = isFavorite(pathname)

  return (
    <div
      className={clsx(
        'page-title d-flex align-items-center flex-wrap me-3 gap-2',
        classes.pageTitle.join(' ')
      )}
    >
      {companyName ? (
        <span className='d-flex align-items-center text-dark fw-bolder fs-3 gap-2'>
          {companyName}
          <i className='bi bi-chevron-double-right fs-6 text-gray-500'></i>
        </span>
      ) : null}

      {hasBack ? (
        typeof backLink === 'function' ? (
          <IconButton iconName='arrow_back' iconSize={18} rounded='circle' onClick={backLink} />
        ) : (
          <IconButton
            iconName='arrow_back'
            rounded='circle'
            iconSize={18}
            onClick={() => navigate(backLink || (-1 as any))}
          />
        )
      ) : null}
      {/* begin::Title */}
      {pageTitle && (
        <h1 className='d-flex align-items-center text-gray-800 fw-bold my-1 fs-6'>
          {pageTitle}
          {pageDescription && config.app?.pageTitle?.description && (
            <>
              {/* begin::Separator */}
              <span className='h-20px border-gray-200 border-start ms-3 mx-2'></span>
              {/* end::Separator */}

              <small className='text-muted fs-7 fw-bold my-1 ms-1'>{pageDescription}</small>
            </>
          )}
        </h1>
      )}
      {/* end::Title */}

      {/* begin::Favorite toggle — add the current page to Favorites */}
      {pageTitle && (
        <span
          role='button'
          className='d-inline-flex align-items-center'
          title={isFav ? 'Remove from favorites' : 'Add to favorites'}
          onClick={() =>
            isFav ? removeByPath(pathname) : addFavorite(pageTitle as string, pathname, 'star')
          }
        >
          <Icon
            icon='star'
            size={22}
            variants={isFav ? 'filled' : 'outlined'}
            color={isFav ? 'warning' : undefined}
            className={isFav ? '' : 'text-gray-500'}
          />
        </span>
      )}
      {/* end::Favorite toggle */}

      {pageBreadcrumbs &&
        pageBreadcrumbs.length > 0 &&
        config.app?.pageTitle?.breadCrumb && (
          <>
            {/* begin::Separator */}
            <span className='h-20px border-gray-200 border-start mx-3'></span>
            {/* end::Separator */}
            <ul className='breadcrumb breadcrumb-separatorless fw-bold fs-7 my-1'>
              {Array.from(pageBreadcrumbs).map((item, index) => (
                <Fragment key={`${item.path}${index}`}>
                  {item.isSeparator ? (
                    <li className='breadcrumb-item'>
                      <span className='bullet bg-gray-200 w-5px h-2px'></span>
                    </li>
                  ) : (
                    <>
                      {item.isActive ? (
                        <li className='breadcrumb-item text-dark'>{item.title}</li>
                      ) : (
                        <li className='breadcrumb-item text-muted'>
                          <Link to={item.path} className='text-muted text-hover-primary'>
                            {item.title}
                          </Link>
                        </li>
                      )}
                    </>
                  )}
                </Fragment>
              ))}
              <li className='breadcrumb-item text-dark'>{pageTitle}</li>
            </ul>
          </>
        )}
    </div>
  )
}

export {DefaultTitle}
